// tools/websocket-connector.tsx
import React, {forwardRef, useEffect, useImperativeHandle, useRef} from 'react';
import {io, Socket} from 'socket.io-client';
import {apiClient} from '@common/http/query-client';

export type EventHandlers = Record<string, (data: any, socket: Socket) => void>;

export interface WebSocketConnectorProps {
  slugOrId: string | number;
  iface: string;
  space: string;
  serverUrl: string;
  eventHandlers?: EventHandlers;
  onConnect?: (socket: Socket) => void;
  onDisconnect?: (reason: any) => void;
  onError?: (err: any) => void;
  antiStrictMode?: boolean;
}

export interface WebSocketHandle {
  emit: (event: string, payload?: any) => void;
  on: (event: string, handler: (data: any) => void) => void;
  off: (event: string, handler?: (data: any) => void) => void;
  connected: () => boolean;
  connect: () => void;
  disconnect: () => void;
  socketId: () => string | undefined;
}

export const WebSocketConnector = forwardRef<WebSocketHandle, WebSocketConnectorProps>(
  function WebSocketConnector(
    {slugOrId, iface, space, serverUrl, eventHandlers, onConnect, onDisconnect, onError, antiStrictMode = true},
    ref
  ) {
    const socketRef = useRef<Socket | null>(null);
    const tokenRef = useRef<string | null>(null);
    const timerRef = useRef<number | null>(null);

    // 🔒 stabiliser callbacks et handlers via refs
    const handlersRef = useRef<EventHandlers>({});
    const onConnectRef = useRef<typeof onConnect>();
    const onDisconnectRef = useRef<typeof onDisconnect>();
    const onErrorRef = useRef<typeof onError>();
    const attachedRef = useRef<EventHandlers>({});

    function applyHandlers(s: Socket, next: EventHandlers) {
      // off anciens
      Object.entries(attachedRef.current).forEach(([evt, cb]) => {
        s.off(evt, cb);
      });
      attachedRef.current = {};

      // on nouveaux
      Object.entries(next || {}).forEach(([evt, cb]) => {
        const wrapped = (payload: any) => cb(payload, s);
        s.on(evt, wrapped);
        attachedRef.current[evt] = wrapped;
      });
    }

    async function fetchToken() {
      const {data} = await apiClient.get(`live-votes/${slugOrId}/server/token`, {
        params: {interface: iface, space: space},
        headers: {Accept: 'application/json'},
        withCredentials: true,
      });
      const token = data?.data?.token ?? data?.token;
      if (!token) throw new Error('token_missing');
      tokenRef.current = token;
      return token;
    }
    
    useEffect(() => { handlersRef.current = eventHandlers || {}; }, [eventHandlers]);
    useEffect(() => { onConnectRef.current = onConnect; }, [onConnect]);
    useEffect(() => { onDisconnectRef.current = onDisconnect; }, [onDisconnect]);
    useEffect(() => { onErrorRef.current = onError; }, [onError]);

    useImperativeHandle(ref, () => ({
      emit(event, payload) { socketRef.current?.emit(event, payload); },
      on(event, handler) { socketRef.current?.on(event, handler); },
      off(event, handler) {
        if (!socketRef.current) return;
        if (handler) socketRef.current.off(event, handler);
        else socketRef.current.removeAllListeners(event);
      },
      connected() { return !!socketRef.current?.connected; },
      connect() { socketRef.current?.connect(); },
      disconnect() { socketRef.current?.disconnect(); },
      socketId() { return socketRef.current?.id; },
    }));

    useEffect(() => {
      let destroyed = false;

      const run = async () => {
        try {
          const token = await fetchToken();
          if (destroyed) return;

          const s = io(serverUrl, {
            auth: {token},
            transports: ['websocket'],
            reconnection: true,
            reconnectionAttempts: Infinity,
            reconnectionDelay: 500,
            reconnectionDelayMax: 5000,
            forceNew: false,
          });
          socketRef.current = s;

          // attacher handlers ICI (une première fois)
          applyHandlers(s, handlersRef.current);

          s.on('connect', () => {
            onConnectRef.current?.(s);
          });
          s.on('disconnect', reason => {
            onDisconnectRef.current?.(reason);
          });
          s.on('connect_error', async err => {
            const msg = (err?.message || '').toLowerCase();
            if (msg.includes('invalid') || msg.includes('jwt') || msg.includes('expired')) {
              try {
                const newToken = await fetchToken();
                if (socketRef.current) {
                  socketRef.current.auth = {token: newToken};
                  socketRef.current.connect();
                  return;
                }
              } catch {}
            }
            onErrorRef.current?.(err);
          });
        } catch (e) {
          onErrorRef.current?.(e);
        }
      };

      run();

      return () => {
        destroyed = true;
        if (timerRef.current) {
          clearTimeout(timerRef.current);
          timerRef.current = null;
        }
        const s = socketRef.current;
        if (s) {
          // off tout proprement
          Object.entries(attachedRef.current).forEach(([evt, cb]) => s.off(evt, cb));
          attachedRef.current = {};
          s.disconnect();
          socketRef.current = null;
        }
      };
    }, [slugOrId, iface, serverUrl, antiStrictMode]);

    // Quand eventHandlers change, re-applique sans doublons
    useEffect(() => {
      const s = socketRef.current;
      if (!s) return;
      applyHandlers(s, handlersRef.current);
    }, [eventHandlers]);

    return null;
  }
);
